const BB_SELECTED_OPTIONS = 'bb_selected_options'

class BundleBuilderHandler {
	constructor() {
		this.options = {}
		this.selectedOptions = {}
		this.currentProduct = null
		this.swatchImageCache = new Map()
		this.productById = new Map()
		this.productByHandle = new Map()
		this.productLookupReady = false
		this._bundleProductsRef = null
		this.summaryToggleBound = false
		this._normalizedExcludingTags = null
		this._addButtonLoadingStartedAt = 0
		this._addButtonAddedTimer = null
		this.ADD_BUTTON_ADDED_MS = 2000
		this.ADD_BUTTON_MIN_LOADING_MS = 250
	}

	// ------------------------------------------------------------ getters
	get bundleProducts() {
		return window.bundleProducts
	}

	get excludingTags() {
		return window.bundleSettings?.excluding_tags
	}

	get normalizedExcludingTags() {
		if (!this._normalizedExcludingTags) {
			const tags = Array.isArray(this.excludingTags) ? this.excludingTags : []
			this._normalizedExcludingTags = tags
				.map(tag => typeof tag === 'string' ? tag.trim() : '')
				.filter(Boolean)
		}

		return this._normalizedExcludingTags
	}

	get groupByQuantity() {
		return window.bundleSettings?.group_by_quantity
	}

	get bundleDiscount() {
		return window?.bundleSettings?.bundle_discount || 3
	}

	get bundleItemLimit() {
		const limit = Number(window?.bundleSettings?.bundle_item_limit)
		return Number.isFinite(limit) && limit > 0 ? limit : 5
	}

	get bodyEl() {
		return this.getCachedElement('_bodyEl', 'body')
	}

	get bundleCategoriesEls() {
		return document.querySelectorAll('#bundle-builder li.grid .li-a')
	}

	get modalBundleItemEl() {
		return this.getCachedElement('_modalBundleItemEl', '#modal-bundle-item')
	}

	get quantitySelectorEl() {
		return this.getCachedElement('_quantitySelectorEl', '#modal-bundle-item quantity-selector')
	}

	get quantityInputEl() {
		return this.getCachedElement('_quantityInputEl', '#modal-bundle-item #qty')
	}

	get modalErrorEl() {
		return this.getCachedElement('_modalErrorEl', '#modal-bundle-item .bundle-modal-error')
	}

	get swatchesWrapperEl() {
		return this.getCachedElement('_swatchesWrapperEl', '#swatches-wrapper')
	}

	get bundleItemsEl() {
		return this.getCachedElement('_bundleItemsEl', '#bundle-items')
	}

	get summaryListEl() {
		return this.getCachedElement('_summaryListEl', '.li-items')
	}

	get showAllButtonEl() {
		return this.getCachedElement('_showAllButtonEl', '#show-all')
	}

	get hiddenItemsButtonEl() {
		return this.getCachedElement('_hiddenItemsButtonEl', '#hidden-items')
	}

	get productOptionsLength() {
		return this.currentProduct.options?.length || Number(this.currentProduct.option_size) || 0
	}

	// ------------------------------------------------------------ setters
	set setCurrentProduct(product) {
		this.currentProduct = product
	}

	// ------------------------------------------------------------ methods
	init() {
		this.createProductLookups()
		this.bundleCategoriesListeners()
		this.modalListeners()
		this.handleBundleItems()
		this.handleUrlParams()
		this.checkoutButtonListener()
		this.bindSummaryToggles()
		this.handleBundleProductsReady()
		this.bindInlineCartListeners()
	}

	bindInlineCartListeners() {
		const handleMinus = async (target) => {
			const index = Number(target.dataset.index)
			const sellingPlan = target.dataset.sellingId || undefined
			const totalQty = Number(target.dataset.totalQuantity || target.getAttribute('data-total-quantity')) || 1
			const newQty = Math.max(totalQty - 1, 0)

			try {
				await fetch('/cart/change.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						line: index,
						quantity: newQty,
						selling_plan: sellingPlan,
						sections: ['cart-drawer', 'cart-bubble'],
						sections_url: window.location.pathname
					})
				})

				document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
			} catch (error) {
				console.error('Bundle builder minus-control failed', error)
			}
		}

		document.removeEventListener('click', this._bundleMinusHandler)
		this._bundleMinusHandler = (event) => {
			const btn = event.target.closest('.minus-control')
			if (!btn) return
			event.preventDefault()
			handleMinus(btn)
		}
		document.addEventListener('click', this._bundleMinusHandler)
	}

	handleBundleItems() {
		if (!this.bundleItemsEl) return

		const self = this

		const handleCartUpdate = async function(event) {
			let cartData = event?.detail?.cartData || event?.detail?.cart

			if (!cartData) {
				try {
					const response = await fetch('/cart.js', { credentials: 'same-origin' })
					if (response.ok) {
						cartData = await response.json()
					}
				} catch (error) {
					console.error('Bundle builder: failed to load cart data', error)
					return
				}
			}

			if (!cartData || !cartData.items) return

			const cartItemsFiltered = cartData.items.filter(item => {
				const cartProduct = self.getProductById(item.product_id)

				if (cartProduct) {
					const tags = cartProduct.tags || ''
					return !self.normalizedExcludingTags.some(tag => tags.includes(tag))
				}

				return false
			})

			function expandArrayByQuantity(inputArray) {
				return inputArray.flatMap((item, index) => {
					const { quantity, ...rest } = item
					return Array.from({ length: quantity }, () => ({
						...rest,
						quantity: 1,
						total_quantity: quantity,
						original_index: index
					}))
				})
			}

			const finalArray = self.groupByQuantity ? cartItemsFiltered : expandArrayByQuantity(cartItemsFiltered)

			const module = finalArray.length % self.bundleItemLimit || self.bundleItemLimit
			const finalModule = module === self.bundleItemLimit && finalArray.length !== self.bundleItemLimit ? 0 : module

			let bundleItemsHTML = ''

			finalArray.forEach((item, index) => {

				let show = finalArray.length > self.bundleItemLimit ? finalModule >= index + 1 : true

				const bundleItemHTML = `
						<a href="/products/${item.handle}"
							class="bundle-item is-product${show ? '' : ' hidden'}"
							data-variant-id="${item.id}"
						>
							<span
								class="bundle-close ${self.groupByQuantity ? 'cart-delete' : 'minus-control'}"
								data-id="${item.id}"
								data-total-quantity="${item.total_quantity || item.quantity}"
								data-selling-id="${item.selling_plan_allocation?.selling_plan.id || ''}"
								data-index="${item.original_index + 1  || index + 1}"
							>
								<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="10" cy="10" r="9" fill="white" stroke="#DCDCDC" stroke-width="2"/>
									<path d="M10.7043 9.99957L12.8522 7.85634C12.9463 7.76227 12.9991 7.63467 12.9991 7.50163C12.9991 7.36859 12.9463 7.241 12.8522 7.14692C12.7582 7.05285 12.6306 7 12.4976 7C12.3646 7 12.237 7.05285 12.1429 7.14692L10 9.29515L7.85707 7.14692C7.76301 7.05285 7.63544 7 7.50241 7C7.36939 7 7.24182 7.05285 7.14776 7.14692C7.0537 7.241 7.00085 7.36859 7.00085 7.50163C7.00085 7.63467 7.0537 7.76227 7.14776 7.85634L9.29568 9.99957L7.14776 12.1428C7.10094 12.1893 7.06378 12.2445 7.03842 12.3054C7.01306 12.3663 7 12.4316 7 12.4975C7 12.5635 7.01306 12.6288 7.03842 12.6896C7.06378 12.7505 7.10094 12.8058 7.14776 12.8522C7.19419 12.899 7.24944 12.9362 7.31031 12.9616C7.37118 12.9869 7.43647 13 7.50241 13C7.56836 13 7.63365 12.9869 7.69452 12.9616C7.75539 12.9362 7.81063 12.899 7.85707 12.8522L10 10.704L12.1429 12.8522C12.1894 12.899 12.2446 12.9362 12.3055 12.9616C12.3664 12.9869 12.4316 13 12.4976 13C12.5635 13 12.6288 12.9869 12.6897 12.9616C12.7506 12.9362 12.8058 12.899 12.8522 12.8522C12.8991 12.8058 12.9362 12.7505 12.9616 12.6896C12.9869 12.6288 13 12.5635 13 12.4975C13 12.4316 12.9869 12.3663 12.9616 12.3054C12.9362 12.2445 12.8991 12.1893 12.8522 12.1428L10.7043 9.99957Z" fill="black"/>
								</svg>
							</span>
	
							<div class="bundle-inner">
								<img src="${item.image}" alt="${item.product_title}">
	
								${self.groupByQuantity ? `<span class="bundle-items-quantity">${item.quantity}</span>` : ''}
							</div>
						 </a>
					`

				bundleItemsHTML = `${bundleItemsHTML}${bundleItemHTML}`
			})


			self.bundleItemsEl.innerHTML = bundleItemsHTML

			renderQuantityThresholdBar()
			renderPlaceholder(finalModule)
			renderShowAllButton(finalArray.length)
			self.renderBundleBuilderThresholdBar(finalArray)
			self.bindInlineCartListeners()
		}

		document.addEventListener('cart:refresh', handleCartUpdate)
		document.addEventListener('updateCart', handleCartUpdate)
		document.addEventListener('line-item:change:end', handleCartUpdate)
		document.addEventListener('cart:item-added', handleCartUpdate)

		void handleCartUpdate();

		function renderQuantityThresholdBar() {
			const quantityThresholdBarEl =
				document.querySelector('#cart-form .quantity-threshold-bar-wrapper') ||
				document.querySelector('.empty-cart-content .quantity-threshold-bar-wrapper')

			if (!quantityThresholdBarEl) return

			const quantityThresholdBarBundleWrapperEl = document.querySelector('#quantity-threshold-bar-bundle-wrapper')
			const quantityThresholdBarModalBundleWrapperEl = document.querySelector('#quantity-threshold-bar-bundle-modal-wrapper')

			if (quantityThresholdBarBundleWrapperEl) quantityThresholdBarBundleWrapperEl.innerHTML = quantityThresholdBarEl.innerHTML
			if (quantityThresholdBarModalBundleWrapperEl) quantityThresholdBarModalBundleWrapperEl.innerHTML = quantityThresholdBarEl.innerHTML
		}

		function renderPlaceholder(module = 0) {
			const bundleItemIsProductEls = self.bundleItemsEl.querySelectorAll('.bundle-item.is-product')
			let placeholdersHTML = ''

			for (let i = 1; i <= self.bundleItemLimit - module ; i++) {
				const placeholderHTML = `
					 <a href="#" class="bundle-item is-placeholder">
						 <div class="bundle-inner">
							<span>${bundleItemIsProductEls.length + i}</span>
						</div>
					</a>
				`

				placeholdersHTML = `${placeholdersHTML}${placeholderHTML}`
			}

			self.bundleItemsEl.innerHTML = `${self.bundleItemsEl.innerHTML}${placeholdersHTML}`
		}

		function renderShowAllButton(count) {
			const summaryListEl = self.summaryListEl
			const showAllButtonsEl = self.showAllButtonEl
			const bundleItemsEl = self.bundleItemsEl

			if (!summaryListEl || !showAllButtonsEl || !bundleItemsEl) return

			if (count > self.bundleItemLimit) {
				summaryListEl.classList.add('collapsed')

				const hiddenProductEls = bundleItemsEl.querySelectorAll('.bundle-item.is-product.hidden')
				const hiddenItemsCount = showAllButtonsEl.querySelector('.hidden-items-count')
				if (hiddenItemsCount) hiddenItemsCount.innerText = hiddenProductEls.length

				const lastHiddenImage = hiddenProductEls[hiddenProductEls.length - 1]?.querySelector('img')
				const showAllButtonsImageEl = showAllButtonsEl.querySelector('img')
				if (lastHiddenImage && showAllButtonsImageEl) showAllButtonsImageEl.src = lastHiddenImage.src
			} else {
				summaryListEl.classList.remove('collapsed', 'expanded')
			}
		}
	}

	bundleCategoriesListeners() {
		this.bundleCategoriesEls.forEach(bundleCategory => {
			bundleCategory.addEventListener('click', (e) => this.handleBundleCategoryClick(e))
		})
	}

	checkoutButtonListener() {
		const checkoutButtonEl = document.getElementById('bundle-builder-checkout-button')
		if (!checkoutButtonEl) return

		checkoutButtonEl.addEventListener('click', () => this.openCartDrawer())
	}

	bindSummaryToggles() {
		if (this.summaryToggleBound) return

		document.addEventListener('click', (event) => {
			const showAllTrigger = event.target.closest('#show-all')
			if (showAllTrigger) {
				event.preventDefault()
				this.expandSummaryList()
				return
			}

			const hideTrigger = event.target.closest('#hidden-items')
			if (hideTrigger) {
				event.preventDefault()
				this.collapseSummaryList()
			}
		})

		this.summaryToggleBound = true
	}

	expandSummaryList() {
		const summaryListEl = this.summaryListEl
		if (!summaryListEl) return

		summaryListEl.classList.remove('collapsed')
		summaryListEl.classList.add('expanded')

		if (this.isMobile()) this.handleShowModalBundleItem(false)
	}

	collapseSummaryList() {
		const summaryListEl = this.summaryListEl
		if (!summaryListEl) return

		summaryListEl.classList.add('collapsed')
		summaryListEl.classList.remove('expanded')
	}

	modalListeners() {
		const self = this

		const closeEl = this.modalBundleItemEl.querySelector('#modal-bundle-item-close')
		closeEl.addEventListener('click', () => this.handleShowModalBundleItem(false))

		const addButtonEl = this.modalBundleItemEl.querySelector('#add-button')
		addButtonEl.addEventListener('click', (event) => {
			// Prevent "sticky hover" visuals on click by clearing focus for pointer-driven clicks.
			// Keyboard activation keeps focus (event.detail === 0).
			if (event && event.detail) {
				addButtonEl.blur?.()
			}
			this.handleAddButton()
		})

		this.modalBundleItemEl.addEventListener('click', function(event) { // detecting a modal outside click and closing it
			const modalEL = self.modalBundleItemEl.querySelector('.modal-bundle-item-inner')
			if (!modalEL.contains(event.target)) self.handleShowModalBundleItem(false)
		})
	}

	getAddButtonTextElement() {
		return this.modalBundleItemEl?.querySelector('#add-button [data-add-button-text]')
			|| this.modalBundleItemEl?.querySelector('#add-button span')
	}

	setAddButtonLabel(text = '') {
		const addButtonTextEl = this.getAddButtonTextElement()
		if (addButtonTextEl) addButtonTextEl.textContent = text
	}

	setAddButtonLoadingState(loading = false) {
		const addButtonWrapperEl = this.modalBundleItemEl?.querySelector('#add-button-wrapper')
		if (!addButtonWrapperEl) return

		if (!loading && this._addButtonLoadingStartedAt) {
			const elapsed = Date.now() - this._addButtonLoadingStartedAt
			const minVisibleMs = this.ADD_BUTTON_MIN_LOADING_MS

			if (elapsed < minVisibleMs) {
				setTimeout(() => this.setAddButtonLoadingState(false), minVisibleMs - elapsed)
				return
			}
		}

		if (loading) this._addButtonLoadingStartedAt = Date.now()
		else this._addButtonLoadingStartedAt = 0

		addButtonWrapperEl.classList.toggle('is-loading', loading)
		addButtonWrapperEl.setAttribute('aria-busy', loading ? 'true' : 'false')

		const addButtonTextEl = this.getAddButtonTextElement()
		if (addButtonTextEl) {
			addButtonTextEl.setAttribute('aria-hidden', loading ? 'true' : 'false')
		}

		const loaderEl = addButtonWrapperEl.querySelector('.c-loading')
		if (loaderEl) {
			loaderEl.hidden = !loading
			loaderEl.setAttribute('aria-hidden', loading ? 'false' : 'true')
		}
	}

	showAddButtonAddedState() {
		clearTimeout(this._addButtonAddedTimer)
		this.setAddButtonLabel('ADDED!')

		this._addButtonAddedTimer = setTimeout(() => {
			this.setAddButtonLabel('ADD TO CART')
		}, this.ADD_BUTTON_ADDED_MS)
	}

	handleBundleCategoryClick(e) {
		e.preventDefault()
		this.renderModalContent(e.currentTarget)
	}

	renderModalContent(currentTarget, swatchClick = false) {
		const productId = currentTarget.getAttribute('data-product-id')
		const swatchesStr = currentTarget.getAttribute('data-swatches')
		const swatches = swatchesStr ? JSON.parse(swatchesStr) : ''

		this.setCurrentProduct = this.getProductById(Number(productId))

		if (this.currentProduct) {
			this.renderSlider()
			this.renderTitle()
			this.renderPrices()
			// this.renderSubscriptionFrequency()
			this.renderOptions()
			this.updateQuantitySelector()
			this.handleViewFullDetails()

			if (!swatchClick) {
				if (swatches) this.renderSwatches(swatches)
				this.handleShowModalBundleItem(true)
				this.adjustModalHeight()
				this.scheduleModalHeightAdjustment()
			}

			this.handleCurrentSwatch()
			this.handleOptionsFromLocalStorage()

			const modalEL = this.modalBundleItemEl.querySelector('.modal-bundle-item-inner')
			if (modalEL) modalEL.scrollTop = 0
		}
	}

	handleCurrentSwatch() {
		const swatchesEls = this.swatchesWrapperEl.querySelectorAll('.swatch')
		const currentSwatch = this.swatchesWrapperEl.querySelector(`.swatch[data-handle="${this.currentProduct.handle}"]`)
		const swatchesBlocksEls = this.swatchesWrapperEl.querySelectorAll('.swatches-block')

		if (swatchesEls.length && currentSwatch) {
			swatchesEls.forEach(swatchEl => swatchEl.classList.remove('current-swatch'))
			currentSwatch.classList.add('current-swatch')

			swatchesBlocksEls.forEach(swatchesBlock => {
				const colorNameEl = swatchesBlock.querySelector('label span.color-name')
				colorNameEl.innerText = swatchesBlock.contains(currentSwatch) ? (currentSwatch.getAttribute('data-value') || '').replace(/-+/g, ' ').trim() : ''
			})
		}
	}

	adjustModalHeight() {
		const modalEL = this.modalBundleItemEl.querySelector('.modal-bundle-item-inner')

		if (!modalEL) return

		const summerHeight = this.isMobile() ? 183 : 122 // 173 = 163px bundle mobile summer height + 10 margin top and + 10 margin bottom / 122 = 102px bundle summer height + 10 margin top and + 10 margin bottom
		const maxHeight = Math.max(0, window.innerHeight - summerHeight)

		// Keep natural height and constrain only by max-height so late content updates
		// (e.g. async threshold fragments) do not leave stale fixed heights with extra scroll.
		modalEL.style.height = 'auto'
		modalEL.style.maxHeight = `${maxHeight}px`
	}

	scheduleModalHeightAdjustment() {
		if (this._modalHeightRaf) cancelAnimationFrame(this._modalHeightRaf)
		if (this._modalHeightTimeout) clearTimeout(this._modalHeightTimeout)

		const run = () => {
			if (!this.modalBundleItemEl?.classList.contains('show')) return
			this.adjustModalHeight()
		}

		this._modalHeightRaf = requestAnimationFrame(() => {
			this._modalHeightRaf = requestAnimationFrame(run)
		})
		this._modalHeightTimeout = setTimeout(run, 180)
	}

	renderSlider() {
		if (this.currentProduct?.images) {
			let sliderHTML = ''

			this.currentProduct.images.forEach(imgUrl => {
				const sliderItemHTML = `
					<img src="${imgUrl}" alt="" class="slider-item">
				`

				sliderHTML = `${sliderHTML}${sliderItemHTML}`
			})

			this.modalBundleItemEl.querySelector('#modal-bundle-item-slider').innerHTML = sliderHTML
		}
	}

	renderTitle() {
		this.modalBundleItemEl.querySelector('#modal-bundle-item-title').innerHTML = `<a href="/products/${this.currentProduct.handle}">${this.currentProduct.title}</a>`
	}

	renderPrices() {
		const initComparePrice = this.currentProduct.compare_at_price > this.currentProduct.price
			? this.currentProduct.compare_at_price
			: this.currentProduct.price
		const initPrice = this.currentProduct.price / 100 - window?.bundleSettings?.bundle_discount

		this.modalBundleItemEl.querySelector('#modal-bundle-item-prices').innerHTML = `
			<span class="line-through">$${(initComparePrice / 100).toFixed(2)}</span>
			<span class="sale">$${initPrice.toFixed(2)}</span>
		`
	}

	renderOptions() {
		const { hideShort, hideTall } = this.getHideFlags({
			product: this.currentProduct,
			settings: window.ShopData?.settings,
			marketHandle: window.ShopData?.market
		})


		const optionsWithValues = this.currentProduct.options_with_values
		const productOptions = this.modalBundleItemEl.querySelector(`#product-options-wrapper`)

		let optionsHTML = ''

		optionsWithValues?.forEach(product_option => {
			const hideLengthBlock = product_option.name === 'Length' && hideShort && hideTall

			let optionsBlockHTML = `
			<div class="options-block"${hideLengthBlock ? ' style="display: none"' : '' }>
				<label for="${product_option.name}" class="name-options">${product_option.name}:
					<span class="selected-option" data-selected-postion="${product_option.position}"></span>
				</label>
				
				<div class="options option-count-${product_option.values?.length}">
				
					${product_option.values?.map(value => {
						if ((value === 'Short' && hideShort) || (value === 'Tall' && hideTall)) return ''
				
						return `
							<div class="option">
								<input
									id="option-${product_option.name.replace(/\s+/g, '-').toLowerCase()}-${value.toLowerCase()}-modal-bundle-item"
									class="input-option"
									autocomplete="off"
									data-option-position="${product_option.position}"
									type="radio"
									name="${product_option.name.toLowerCase()}-quick-shop"
									value="${value}"
								>
							
								<label class="label-option" for="option-${product_option.name.replace(/\s+/g, '-').toLowerCase()}-${value.toLowerCase()}-modal-bundle-item"><span>${this.sizeSimplify(value)}</span></label>
							</div>
						`
					}).join('')}
					
				</div>
			</div>
		`

			optionsHTML = `${optionsHTML}${optionsBlockHTML}`
		})

		productOptions.innerHTML = optionsHTML

		this.optionsAddEventListener()

		if (hideShort && hideTall) {
			const lengthRegularEl = document.querySelector('#option-length-regular-modal-bundle-item')

			if (lengthRegularEl) {
				lengthRegularEl.checked = true
				lengthRegularEl.dispatchEvent(new Event('change', { bubbles: true }))
			}
		}
	}

	renderSwatches(swatches) {
		let optionsHTML = ''
		const availableSwatches = swatches.filter(item => item?.swatch_name?.toString().trim().toLowerCase() !== 'packs')

		availableSwatches.forEach(item => {
			const displayMode = (item?.display_mode || 'color').toString().trim().toLowerCase()

			let optionsBlockHTML = `
						<div class="swatches-block">
							<label>${item.swatch_name}: <span class="color-name"></span></label>
							
							<div class="swatches">
							
								${item.colour_list?.split(',')?.map(value => {
									const swatch = value.split('_') // EXAMPLE: black_#212223_black-crew-neck
									const swatchObj = {
										colorName: swatch[0],
										color: swatch[1],
										handle: swatch[2],
									}
					
									const initProduct = this.getProductByHandle(swatchObj?.handle)
					
									if (initProduct) {
										const imageData = displayMode === 'image' ? this.getSwatchImage(initProduct) : null
										const imageAttr = imageData ? `
											<span class="product-swatch-button__thumb">
												<img
													src="${imageData.src}"
													${imageData.srcset ? `srcset="${imageData.srcset}"` : ''}
													${imageData.sizes ? `sizes="${imageData.sizes}"` : ''}
													alt="${initProduct.title || swatchObj.colorName}"
													loading="lazy"
													decoding="async"
													fetchpriority="low"
												/>
											</span>
										` : `
											<span class="product-swatch-button__thumb" style="${this.handleSwatchStyle(swatchObj.color)}"></span>
										`
										return `
											<div
												data-product-id="${initProduct.id}"
												data-handle="${swatchObj.handle}"
												data-value="${swatchObj.colorName}"
												class="swatch"
											>
												${imageAttr}
											</div>
										`
									}
					
								}).join('')}
								
							</div>
						</div>
					`

			optionsHTML = `${optionsHTML}${optionsBlockHTML}`
		})

		this.swatchesWrapperEl.innerHTML = optionsHTML
		this.swatchesAddEventListener()
	}

	renderSubscriptionFrequency() {
		const hasSellingPlans = this.currentProduct.selling_plan_groups?.length > 0
		const subscriptionFrequencyEl = this.modalBundleItemEl.querySelector(`#subscription-frequency-wrapper`)

		if (hasSellingPlans) {
			const isSubOnly = this.currentProduct.requires_selling_plan
			const currentPlanDiscount = this.currentProduct?.selling_plan_groups[0]?.selling_plans[0]?.price_adjustments[0]?.value

			subscriptionFrequencyEl.innerHTML = `
				<div id="subscription_frequancy" class="subscription_frequancy">
					<label class="subscription_frequancy_label">Frequency:</label>

					<div class="subscription_frequancy_items">
						${isSubOnly ? '' : `
							<div class="subscription_frequancy_item" data-sub-type="onetime">
								<div class="subscription_frequancy_trigger is-Active">
									<span class="radio__circle"></span> 
									<input type="radio" checked name="subscription-radio" class="radio__input" value="onetime">
									<span class="subscription_frequancy_triggerText">One-Time Purchase</span>
								</div>
							</div>
						`}

						<div class="subscription_frequancy_item" data-sub-type="subscription">
							<div class="subscription_frequancy_trigger${isSubOnly ? ' is-Active' : ''}">
								<span class="radio__circle"></span>
								<input type="radio" ${isSubOnly ? 'checked' : ''} name="subscription-radio" class="radio__input" value="subscription">
								<div class="subscription_frequancy_textWrapper${isSubOnly ? ' subscription-only' : ''}">
								    <span class="subscription_frequancy_triggerText">Subscribe and </span> 
								    <span class="subscription_frequancy_triggerHighlight">${isSubOnly ? 'Get Free Shipping' : `Save ${currentPlanDiscount}% + Get Free Shipping`}</span>
								</div>
							</div>
							
							<div class="frequency_content${isSubOnly ? '' : ' hidden'}">
								<div class="frequency_buttons_wrapper">
									${this.currentProduct?.selling_plan_groups[0]?.selling_plans?.map((plan, index) => {
										return `
												<span
													data-selling-plan-name="${plan.name}"
													data-selling-plan-id="${plan.id}"
													class="frequency_button${index === 0 ? ' is-Active' : ''}"
												>
													${plan.name.replace('Delivery every ', '')}
												</span>
											`
										}).join('')}
										</div>
									</div>
							</div>
						</div>
					</div>
				</div>
			`

			this.subscriptionFrequencyAddEventListener(isSubOnly)
		} else subscriptionFrequencyEl.innerHTML = ''
	}

	handleViewFullDetails() {
		this.modalBundleItemEl.querySelector('#view-full-details a').href = `/products/${this.currentProduct.handle}`
	}

	optionsAddEventListener() {
		this.modalBundleItemEl.querySelectorAll('.input-option')?.forEach(option => {
			option.addEventListener('change', e => {
				this.setOption(e.currentTarget)
				this.setOptionsInLocalStorage(e.currentTarget)
			})
		})
	}

	swatchesAddEventListener() {
		this.modalBundleItemEl.querySelectorAll('.swatch')?.forEach(swatch => {
			swatch.addEventListener('click', e => {
				this.renderModalContent(e.currentTarget, true)
			})
		})
	}

	subscriptionFrequencyAddEventListener(isSubOnly) {
		if (!isSubOnly) {
			const oneTimeTriggerEl = this.modalBundleItemEl.querySelector('#subscription_frequancy div[data-sub-type=onetime] .subscription_frequancy_trigger')
			const subTriggerEl = this.modalBundleItemEl.querySelector('#subscription_frequancy div[data-sub-type=subscription] .subscription_frequancy_trigger')
			const subContentEl = this.modalBundleItemEl.querySelector('#subscription_frequancy .frequency_content')

			this.modalBundleItemEl.querySelectorAll('#subscription_frequancy .radio__input')?.forEach(item => {
				item.addEventListener('change', e => {
					oneTimeTriggerEl.classList.toggle('is-Active')
					subTriggerEl.classList.toggle('is-Active')
					subContentEl.classList.toggle('hidden')
				})
			})
		}

		const frequencyButtonsEls = this.modalBundleItemEl.querySelectorAll('#subscription_frequancy .frequency_content .frequency_button')

		frequencyButtonsEls?.forEach(frequencyButton => {
			frequencyButton.addEventListener('click', e => {
				frequencyButtonsEls.forEach(b => b.classList.remove('is-Active'))
				e.currentTarget.classList.add('is-Active')
			})
		})
	}

	setOption(currentTarget) {
		const value = currentTarget.value
		const position = currentTarget.getAttribute('data-option-position')

		const selectedSpan = this.modalBundleItemEl.querySelector(`span[data-selected-postion="${position}"]`)
		if (selectedSpan) selectedSpan.innerText = value

		this.options[`option${position}`] = value

		this.checkSoldout()
		this.checkSwatchAvailable()
		this.checkSelectedAllOptions()
	}

	setOptionsInLocalStorage(currentTarget) {
		const optionName = currentTarget.getAttribute('name')
		const value = currentTarget.value

		const initOption = {
			optionName,
			value
		}

		this.selectedOptions[initOption.optionName] = value

		let selectedProductOptions = this.getBBSelectedOptions()

		if (selectedProductOptions) {
			const modifyProperty = (arr, optionName, value) => {
				const targetObj = arr.find(obj => obj.optionName === optionName)

				if (targetObj) targetObj.value = value
				else selectedProductOptions.push(initOption)
			}

			modifyProperty(selectedProductOptions, initOption.optionName, initOption.value)
		} else selectedProductOptions = [initOption]

		this.setBBSelectedOptions(selectedProductOptions)
	}

	handleOptionsFromLocalStorage() {
		const selectedProductOptions = this.getBBSelectedOptions()

		if (selectedProductOptions) {
			selectedProductOptions.forEach(o => this.setOptionFromLocalStorage(o))
		} else {
			this.setOptionFromLocalStorage({ optionName: 'length', value: 'Regular' }) // default value
		}
	}

	setOptionFromLocalStorage(productVariant) {
		const value = productVariant.value
		const optionName = productVariant.optionName
		const input = document.querySelector(`input[value="${value}"][name="${optionName}"]`)

		if (input) {
			const position = input.getAttribute('data-option-position')
			input.checked = true

			const selectedSpan = this.modalBundleItemEl.querySelector(`span[data-selected-postion="${position}"]`)
			if (selectedSpan) selectedSpan.innerText = value

			this.options[`option${position}`] = value;
			this.selectedOptions[optionName] = value;

			this.checkSwatchAvailable()
			this.checkSoldout()
			this.checkSelectedAllOptions()
		}
	}

	checkSoldout() {
		this.modalBundleItemEl.querySelectorAll('.input-option')?.forEach(option => {
			const initOptions = { ...this.options }

			const value = option.value
			const position = option.getAttribute('data-option-position')

			initOptions[`option${position}`] = value

			const variantFiltered = this.productVariantsHandler(initOptions, this.currentProduct, 'filter')

			if (variantFiltered.length) {
				const available = variantFiltered.some(e => e.available)

				if (available) {
					option.parentElement.classList.remove('soldout')
					option.parentElement.classList.add('available')
				} else {
					option.parentElement.classList.remove('available')
					option.parentElement.classList.add('soldout')
				}
			}
		})
	}

	checkSwatchAvailable() {
		const wrapperEl = this.swatchesWrapperEl
		if (!wrapperEl) return

		const swatchEls = wrapperEl.querySelectorAll('.swatch[data-handle]')
		if (!swatchEls.length) return

		swatchEls.forEach(swatchEl => {
			const handle = swatchEl.getAttribute('data-handle')
			const product = this.getProductByHandle(handle)
			if (!product) return

			const variantFiltered = this.productVariantsHandler({ ...this.options }, product, 'filter') || []
			const available = variantFiltered.some(e => e.available)

			swatchEl.classList.toggle('swatch-not-available', !available)
		})

		this.checkSwatchAvailableForBlock()
	}

	checkSwatchAvailableForBlock() {
		const wrapperEl = this.swatchesWrapperEl
		if (!wrapperEl) return

		const swatchesEls = wrapperEl.querySelectorAll('.swatches-block')

		if (!swatchesEls.length) return

		swatchesEls.forEach(s => {
			const swatchEls = s.querySelectorAll('.swatch')
			const swatchSizeNotAvailableEls = s.querySelectorAll('.swatch.swatch-not-available')

			s.style.display = swatchEls.length === swatchSizeNotAvailableEls.length ? 'none' : 'block'
		})
	}


	checkSelectedAllOptions() {
		const optionsLength = Object.keys(this.options).length

		if (this.productOptionsLength <= optionsLength) this.getVariant()
	}

	getVariant() {
		const variant = this.productVariantsHandler(this.options, this.currentProduct, 'find')

		this.handleButtons(variant)
	}

	handleSwatchStyle(color = '') {
		if (!color.includes('-')) return `background-color: ${color};`

		const parts = color.split('-')
		const type = parts[0]

		switch (type) {
			case 'striped':
			case 'stripedBig':
				let stripedAngle =  parts[1]
				let stripedColor1 =  parts[2]
				let stripedColor2 =  parts[3]
				let stripedWidth = type === 'stripedBig' ? 6 : 3
				let stripedSize = type === 'stripedBig' ? 10 : 5

				return `
					background: repeating-linear-gradient(${stripedAngle}, ${stripedColor1}, ${stripedColor1} ${stripedWidth}px, ${stripedColor2} ${stripedWidth}px, ${stripedColor2} ${stripedWidth * 2}px);
					background-size: ${stripedSize}px;
				  `

			case 'gingham':
				let ginghamColor1 =  parts[1]
				let ginghamColor2 =  parts[2]

				return `
					background-image:
					  repeating-linear-gradient(0deg, ${ginghamColor1}80 0 4px, transparent 4px 8px),
					  repeating-linear-gradient(90deg, ${ginghamColor2}80 0 4px, transparent 4px 8px);
					background-color: ${ginghamColor2};
				  `

			default:
				return `background-color: ${color};`
		}
	}

	handleButtons(variant = {}) {
		const addButtonEl = this.modalBundleItemEl.querySelector('#add-button')
		const addButtonTextEl = this.getAddButtonTextElement()

		if (variant?.available) {
			addButtonTextEl.innerText = 'ADD TO CART'
			addButtonEl.removeAttribute('disabled')
		} else {
			addButtonTextEl.innerText = 'SELECT OPTIONS'
			addButtonEl.setAttribute('disabled', 'disabled')
		}

		this.updateQuantitySelector(variant)
	}

	updateQuantitySelector(variant = null) {
		const qtyInput = this.quantityInputEl
		if (!qtyInput) return

		const hasVariant = Boolean(variant && variant.id)
		const quantityRule = hasVariant ? variant?.quantity_rule || {} : {}

		const min = Number.isFinite(Number(quantityRule?.min)) ? Number(quantityRule.min) : 1
		const step = Number.isFinite(Number(quantityRule?.increment)) ? Number(quantityRule.increment) : 1
		const normalizedStep = step > 0 ? step : 1
		const hasMax = Number.isFinite(Number(quantityRule?.max))
		const max = hasMax ? Number(quantityRule.max) : null

		let value = hasVariant ? parseInt(qtyInput.value, 10) : NaN
		if (!hasVariant || !Number.isFinite(value)) value = min

		if (value < min) value = min
		if (max && value > max) value = max

		if (normalizedStep > 1) {
			const offset = (value - min) % normalizedStep
			if (offset !== 0) value = value - offset
		}

		qtyInput.value = value
		qtyInput.min = min
		qtyInput.step = normalizedStep

		if (max) qtyInput.max = max
		else qtyInput.removeAttribute('max')

		const quantitySelectorEl = this.quantitySelectorEl
		if (quantitySelectorEl) {
			quantitySelectorEl.step = normalizedStep

			if (typeof quantitySelectorEl.validateQtyRules === 'function') {
				quantitySelectorEl.validateQtyRules()
			}
		}
	}

	async handleAddButton() {
		const variant = this.productVariantsHandler(this.options, this.currentProduct, 'find')
		const qtyVal = parseInt(this.modalBundleItemEl.querySelector('#qty')?.value, 10)
		const quantity = Number.isFinite(qtyVal) ? qtyVal : 1

		if (!variant) {
			this.handleAddToCartError('Please select available options.')
			return
		}

		this.handleAddToCartError('')
		await this.addVariantToCart({
			variantId: variant.id,
			quantity,
			sellingPlanId: this.getSellingPlanId()
		})
	}

	getSellingPlanId() {
		const value = this.modalBundleItemEl.querySelector('#subscription_frequancy .radio__input:checked')?.value
		const sellingPlanId = this.modalBundleItemEl.querySelector('div[data-sub-type=subscription] .is-Active.frequency_button')?.getAttribute('data-selling-plan-id')

		return value === 'subscription' && sellingPlanId ? sellingPlanId : ''
	}

	handleAddToCartError(message = '') {
		const errorEl = this.modalErrorEl
		if (!errorEl) return

		if (message) {
			errorEl.textContent = message
			errorEl.removeAttribute('hidden')
		} else {
			errorEl.textContent = ''
			errorEl.setAttribute('hidden', 'hidden')
		}
	}

	async addVariantToCart({ variantId, quantity = 1, sellingPlanId = '' }) {
		this.setAddButtonLoadingState(true)

		const config = {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				'Accept': 'application/javascript'
			}
		}

		const formData = new FormData()
		formData.append('id', variantId)
		formData.append('quantity', quantity)
		if (sellingPlanId) formData.append('selling_plan', sellingPlanId)

		const sections = this.getSectionsToRender()
		if (sections?.length) {
			formData.append('sections', sections.map(section => section.section))
			formData.append('sections_url', window.location.pathname)
		}

		config.body = formData

		try {
			const response = await fetch(`${theme.routes.cart_add_url}`, config)
			const data = await response.json()

			if (data?.status) {
				this.handleAddToCartError(data.description || data.message || 'Unable to add item to cart.')

				if (data.status === 422) {
					document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
				}

				return
			}

			this.renderSections(data)
			this.showAddButtonAddedState()

			if (typeof dispatchCustomEvent === 'function') {
				dispatchCustomEvent('cart:item-added', {
					product: data.hasOwnProperty('items') ? data.items[0] : data
				})
			}

			document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
		} catch (error) {
			console.error('Bundle Builder: failed to add item to cart', error)
			this.handleAddToCartError('Unable to add item to cart. Please try again.')
		} finally {
			this.setAddButtonLoadingState(false)
		}
	}

	getSectionsToRender() {
		return [
			{
				id: 'Cart',
				section: 'main-cart',
				selector: '.thb-cart-form'
			},
			{
				id: 'Cart-Drawer',
				section: 'cart-drawer',
				selector: '.cart-drawer'
			},
			{
				id: 'cart-drawer-toggle',
				section: 'cart-bubble',
				selector: '.thb-item-count'
			}
		]
	}

	renderSections(parsedState) {
		const sections = this.getSectionsToRender()
		if (!Array.isArray(sections) || !parsedState?.sections) return

		sections.forEach(section => {
			const sectionEl = document.getElementById(section.id)
			if (!sectionEl || !parsedState.sections[section.section]) return

			const elementToReplace = sectionEl.querySelector(section.selector) || sectionEl
			elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector)

			if (section.id === 'Cart-Drawer') {
				document.getElementById('Cart-Drawer')?.notesToggle()
				document.getElementById('Cart-Drawer')?.removeProductEvent()
			}

			if (section.id === 'Cart' && typeof Cart !== 'undefined') {
				new Cart().renderContents(parsedState)
			}
		})

		//this.openCartDrawer()
	}

	openCartDrawer() {
		const cartDrawerEl = document.getElementById('Cart-Drawer')
		if (!cartDrawerEl) return

		document.body.classList.add('open-cc', 'open-cart')
		cartDrawerEl.classList.add('active')
		cartDrawerEl.focus?.()
		cartDrawerEl.querySelector('.product-recommendations--full')?.classList.add('active')
		if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('cart-drawer:open')
	}

	getSectionInnerHTML(html, selector = '.shopify-section') {
		return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || ''
	}

	productVariantsHandler(initOptions, product, method) {
		const variants = product.variants

		if (!initOptions || !variants) return false

		return variants[method](variant => {
			// checking for out of stock when if first option is not selected
			const optionsKeys = Object.keys(initOptions)

			if (optionsKeys.length === 1 || this.productOptionsLength === 1) {
				const optionKey = optionsKeys[0]
				return variant[optionKey] === initOptions[optionKey]
			}

			let result = false

			if (initOptions['option1']) {
				result = this.handleVariant(variant.option1) === initOptions['option1']
			}

			if (initOptions['option1'] && initOptions['option2']) {
				result =
					this.handleVariant(variant.option1) === initOptions['option1'] &&
					this.handleVariant(variant.option2) === initOptions['option2']
			}

			if (initOptions['option1'] && initOptions['option2'] && initOptions['option3']) {
				result =
					this.handleVariant(variant.option1) === initOptions['option1'] &&
					this.handleVariant(variant.option2) === initOptions['option2'] &&
					this.handleVariant(variant.option3) === initOptions['option3']
			}

			return result
		})
	}

	handleShowModalBundleItem(show = false) {
		this.bodyEl.classList[show ? 'add' : 'remove']('body-modal-bundle-item-showing')
		this.modalBundleItemEl.classList[show ? 'add' : 'remove']('show')
	}


	renderBundleBuilderThresholdBar(items = []) {
		const itemsCount = this.calculateCartCount(items)
		const BBThresholdBarEl = document.querySelector('#bundle-builder-threshold-bar')

		if (!BBThresholdBarEl) return

		const isUnlocked = itemsCount >= this.bundleItemLimit
		BBThresholdBarEl.querySelector('.bundle-builder-threshold-bar').classList[isUnlocked ? 'add' : 'remove']('unlocked')


		// handle an unlocked message
		const unlockedMessageEl = BBThresholdBarEl.querySelector('.bundle-builder-threshold-bar__unlockedMessage')

		if (isUnlocked) {
			unlockedMessageEl.innerHTML = `YOU HAVE UNLOCKED <span>$${itemsCount * this.bundleDiscount} OFF</span>`
			unlockedMessageEl.style.display = 'block'
		} else {
			unlockedMessageEl.style.display = 'none'
		}


		// handle a threshold bar message
		const thresholdBarMessageEl = BBThresholdBarEl.querySelector('.bundle-builder-threshold-bar__message')

		if (isUnlocked) {
			thresholdBarMessageEl.innerHTML = `ADD MORE TO <span>SAVE MORE</span>`
		} else {
			const remainingItems = this.bundleItemLimit - itemsCount
			thresholdBarMessageEl.innerHTML = `YOU ARE ONLY <span>${remainingItems} ${remainingItems === 1 ? 'item' : 'items'}</span> AWAY FROM <span>$${this.bundleItemLimit * this.bundleDiscount} OFF</span>`
		}

		// handle progress bar width
		const progressBarEl = BBThresholdBarEl.querySelector('.bundle-builder-threshold-bar__progressBar')
		if (progressBarEl) {
			const clampedItemsCount = Math.min(Math.max(itemsCount, 0), this.bundleItemLimit)
			let progressWidth = 0

			if (clampedItemsCount >= this.bundleItemLimit) {
				progressWidth = 100
			} else if (clampedItemsCount > 0) {
				const progressTrackEl = BBThresholdBarEl.querySelector('.bundle-builder-threshold-bar__progress')
				const stepEls = BBThresholdBarEl.querySelectorAll('.step-top-wrapper .step-top')
				const targetStepEl = stepEls[clampedItemsCount - 1]

				if (progressTrackEl && targetStepEl && progressTrackEl.offsetWidth > 0) {
					// Tick is rendered from the center of .step-top (:after with left: 50%),
					// so align progress to that visual center instead of label's `left`.
					const tickCenterX = targetStepEl.offsetLeft + (targetStepEl.offsetWidth / 2)
					progressWidth = (tickCenterX / progressTrackEl.offsetWidth) * 100
				}
			}

			progressBarEl.style.width = `${Math.min(Math.max(progressWidth, 0), 100)}%`
		}

		// show bundle builder threshold bar
		BBThresholdBarEl.style.visibility = 'visible'

		const BBThresholdBarModalWrapperEl = document.querySelector('#bundle-builder-threshold-bar-modal-wrapper')
		if (BBThresholdBarModalWrapperEl) BBThresholdBarModalWrapperEl.innerHTML = BBThresholdBarEl.innerHTML
		this.scheduleModalHeightAdjustment()
	}

	calculateCartCount(items = []) {
		return items.reduce((total, item) => {
			const cartProduct = this.getProductById(item.product_id)
			if (!cartProduct) return total

			const tags = cartProduct.tags || ''
			const shouldExclude = this.normalizedExcludingTags.some(tag => tags.includes(tag))
			if (shouldExclude) return total

			// Always count actual cart quantity. `total_quantity` is a helper for expanded UI rows and would double-count here.
			const quantity = item.quantity || 0
			return total + quantity
		}, 0)
	}

	getBBSelectedOptions() {
		return JSON.parse(localStorage.getItem(BB_SELECTED_OPTIONS))
	}

	setBBSelectedOptions(options = null) {
		localStorage.setItem(BB_SELECTED_OPTIONS, JSON.stringify(options))
	}

	handleVariant(variant) {
		return variant?.replace('\"', '')
	}

	handleUrlParams() {
		const urlParams = new URLSearchParams(window.location.search)
		const openCategoryHandleParam = urlParams.get('openCategoryHandle')

		if (openCategoryHandleParam) {
			const normalizedHandle = openCategoryHandleParam.trim().toLowerCase()
			const targetLinkByHandle = [...this.bundleCategoriesEls].find((el) => {
				const handle = (el.getAttribute('data-product-handle') || '').trim().toLowerCase()
				return handle === normalizedHandle
			})

			if (targetLinkByHandle) {
				this.activateTabForElement(targetLinkByHandle)
				targetLinkByHandle.click()
				return
			}
		}
	}

	activateTabForElement(element) {
		const isWomenTarget = Boolean(element?.closest('#bundle-section-wrapper-women'))
		const isMenTarget = Boolean(element?.closest('#bundle-section-wrapper-men'))
		const tabName = isWomenTarget ? 'women' : (isMenTarget ? 'men' : '')

		if (!tabName) return

		const toggleRoot = document.querySelector('#bundle-builder .men-women-toggle')
		if (!toggleRoot) return

		toggleRoot.querySelectorAll('.toggle').forEach((tab) => tab.classList.remove('active-tab'))
		document.querySelectorAll('#bundle-builder .bundle-section-wrapper').forEach((content) => content.classList.remove('active-content'))

		toggleRoot.querySelector(`.${tabName}-toggle`)?.classList.add('active-tab')
		document.getElementById(`bundle-section-wrapper-${tabName}`)?.classList.add('active-content')
	}

	getHideFlags({ product, settings, marketHandle }) {
		let hideShort = false
		let hideTall = false

		const hideShortMarkets = product?.hide_short_for_markets || []
		const hideTallMarkets = product?.hide_tall_for_markets || []

		const resolveMarketHandle = (market) => {
			const key = `${market.title.toLowerCase()}_market_handle`
			return settings?.[key]
		}

		for (const market of hideShortMarkets) {
			if (resolveMarketHandle(market) === marketHandle) {
				hideShort = true
				break
			}
		}

		for (const market of hideTallMarkets) {
			if (resolveMarketHandle(market) === marketHandle) {
				hideTall = true
				break
			}
		}

		return { hideShort, hideTall }
	}

	isMobile() {
		return window.innerWidth < 491
	}

	sizeSimplify(size = '') {
		if (!size) return

		const initSize = size.toLowerCase()

		switch (true) {
			case initSize === 'xsmall':
				return 'XS'

			case initSize === 'small':
				return 'S'

			case initSize === "medium":
				return 'M'

			case initSize === 'large':
				return 'L'

			case initSize === "xlarge":
				return 'XL'

			case initSize === "2xlarge":
				return '2XL'

			case initSize === "3xlarge":
				return '3XL'

			case initSize === "4xlarge":
				return '4XL'

			case initSize === "5xlarge":
				return '5XL'

			default:
				return size
		}
	}

	getSwatchImage(product) {
		if (!product) return null

		const cached = this.swatchImageCache.get(product.handle)
		if (cached) return cached

		const collectSrc = () => {
			if (product.featured_image) {
				if (typeof product.featured_image === 'string') return product.featured_image
				if (product.featured_image.src) return product.featured_image.src
			}

			if (Array.isArray(product.images)) {
				const firstImage = product.images.find((image) => {
					if (!image) return false
					if (typeof image === 'string') return true
					return !!image.src
				})

				if (firstImage) return typeof firstImage === 'string' ? firstImage : firstImage.src
			}

			if (Array.isArray(product.media)) {
				const firstMedia = product.media.find((mediaItem) => mediaItem && mediaItem.src)
				if (firstMedia) return firstMedia.src
			}

			return ''
		}

		let src = collectSrc()
		if (!src) return null

		if (src.startsWith('//')) src = 'https:' + src

		const cleanSrc = src.split('?')[0]
		const widths = [80, 160, 240]
		const params = (w) => `?width=${w}&height=${w}`

		const imageData = {
			src: `${cleanSrc}${params(80)}`,
			srcset: widths.map((w) => `${cleanSrc}${params(w)} ${w}w`).join(', '),
			sizes: '(min-width: 990px) 64px, 55px'
		}

		this.swatchImageCache.set(product.handle, imageData)

		return imageData
	}

	createProductLookups(force = false) {
		const products = Array.isArray(this.bundleProducts) ? this.bundleProducts : null

		if (!products || !products.length) {
			if (force) {
				this.productById.clear()
				this.productByHandle.clear()
			}

			this.productLookupReady = false
			this._bundleProductsRef = null
			return
		}

		if (!force && this.productLookupReady && this._bundleProductsRef === products) return

		this.productById.clear()
		this.productByHandle.clear()

		products.forEach(product => {
			if (!product) return

			if (product.id) this.productById.set(Number(product.id), product)
			if (product.handle) this.productByHandle.set(String(product.handle), product)
		})

		this.productLookupReady = true
		this._bundleProductsRef = products
	}

	getProductById(id) {
		this.createProductLookups()

		const numericId = Number(id)
		return this.productById.get(numericId) || null
	}

	getProductByHandle(handle) {
		this.createProductLookups()

		if (!handle) return null
		return this.productByHandle.get(String(handle)) || null
	}

	getCachedElement(prop, selector) {
		if (this[prop] && document.contains(this[prop])) return this[prop]

		const element = document.querySelector(selector)
		this[prop] = element || null
		return this[prop]
	}

	handleBundleProductsReady() {
		const hasProducts = Array.isArray(this.bundleProducts) && this.bundleProducts.length > 0
		if (hasProducts) {
			this.createProductLookups(true)
			return
		}

		const onReady = () => {
			this.createProductLookups(true)
			document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
		}

		document.addEventListener('bundleProducts:ready', onReady, { once: true })
	}
}


const initBundleBuilderHandler = new BundleBuilderHandler()
initBundleBuilderHandler.init()
